import type { Provider, TransactionRequest } from '@ethersproject/providers';
import type { Overrides, Signer } from 'ethers';
import { Contract, ContractFactory, utils } from 'ethers';

import type { Voting, VotingInterface } from '../Voting';

const _abi = [
  {
    inputs: [
      {
        internalType: 'address',
        name: '_admin',
        type: 'address',
      },
      {
        internalType: 'address',
        name: '_stars',
        type: 'address',
      },
      {
        internalType: 'address',
        name: '_mgl',
        type: 'address',
      },
      {
        internalType: 'address',
        name: '_votingMasterChef',
        type: 'address',
      },
    ],
    stateMutability: 'nonpayable',
    type: 'constructor',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'address',
        name: 'userAddress',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'address payable',
        name: 'relayerAddress',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'bytes',
        name: 'functionSignature',
        type: 'bytes',
      },
    ],
    name: 'MetaTransactionExecuted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'previousAdminRole',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'newAdminRole',
        type: 'bytes32',
      },
    ],
    name: 'RoleAdminChanged',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'sender',
        type: 'address',
      },
    ],
    name: 'RoleGranted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'sender',
        type: 'address',
      },
    ],
    name: 'RoleRevoked',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'address',
        name: 'voter',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'starsAmountMantissa',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'quadraticVoteScore',
        type: 'uint256',
      },
    ],
    name: 'Unvoted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'address',
        name: 'voter',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'starsAmountMantissa',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'quadraticVoteScore',
        type: 'uint256',
      },
    ],
    name: 'Voted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'VotingRoundCanceled',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'uint256[5]',
        name: 'ids',
        type: 'uint256[5]',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'startVoteBlockNum',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'endVoteBlockNum',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'starsRewards',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'votingRound',
        type: 'uint256',
      },
    ],
    name: 'VotingRoundCreated',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'VotingRoundExecuted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'VotingRoundPaused',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'VotingRoundUnpaused',
    type: 'event',
  },
  {
    inputs: [],
    name: 'DEFAULT_ADMIN_ROLE',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'MAX',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'ROLE_ADMIN',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'userAddress',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'calculateStarsRewards',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'cancelVotingRound',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256[5]',
        name: 'ids',
        type: 'uint256[5]',
      },
      {
        internalType: 'uint256',
        name: 'startVoteBlockNum',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'endVoteBlockNum',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'starsRewards',
        type: 'uint256',
      },
    ],
    name: 'createNewVotingRound',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'userAddress',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'votingRoundId',
        type: 'uint256',
      },
    ],
    name: 'didUserClaimRewards',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'userAddress',
        type: 'address',
      },
      {
        internalType: 'bytes',
        name: 'functionSignature',
        type: 'bytes',
      },
      {
        internalType: 'bytes32',
        name: 'sigR',
        type: 'bytes32',
      },
      {
        internalType: 'bytes32',
        name: 'sigS',
        type: 'bytes32',
      },
      {
        internalType: 'uint8',
        name: 'sigV',
        type: 'uint8',
      },
    ],
    name: 'executeMetaTransaction',
    outputs: [
      {
        internalType: 'bytes',
        name: '',
        type: 'bytes',
      },
    ],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'executeVotingRound',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getChainID',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'votingRoundId',
        type: 'uint256',
      },
    ],
    name: 'getIds',
    outputs: [
      {
        internalType: 'uint256[5]',
        name: '',
        type: 'uint256[5]',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'user',
        type: 'address',
      },
    ],
    name: 'getNonce',
    outputs: [
      {
        internalType: 'uint256',
        name: 'nonce',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
    ],
    name: 'getRoleAdmin',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'userAddress',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'votingRoundId',
        type: 'uint256',
      },
    ],
    name: 'getUserTotalStarsEntered',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'votingRoundId',
        type: 'uint256',
      },
    ],
    name: 'getVotes',
    outputs: [
      {
        internalType: 'uint256[5]',
        name: '',
        type: 'uint256[5]',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'votingRoundId',
        type: 'uint256',
      },
    ],
    name: 'getVotingRound',
    outputs: [
      {
        internalType: 'uint256[5]',
        name: '',
        type: 'uint256[5]',
      },
      {
        internalType: 'uint256[5]',
        name: '',
        type: 'uint256[5]',
      },
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
      {
        internalType: 'enum Voting.VotingRoundState',
        name: '',
        type: 'uint8',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'grantRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'hasRole',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'mgl',
    outputs: [
      {
        internalType: 'contract IERC1155',
        name: '',
        type: 'address',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'pauseVotingRound',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'starsAmountMantissa',
        type: 'uint256',
      },
      {
        internalType: 'bool',
        name: 'isMogulSmartWallet',
        type: 'bool',
      },
      {
        internalType: 'address',
        name: 'mogulSmartWalletAddress',
        type: 'address',
      },
    ],
    name: 'removeVoteFor',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'renounceRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'revokeRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'stars',
    outputs: [
      {
        internalType: 'contract IERC20',
        name: '',
        type: 'address',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes4',
        name: 'interfaceId',
        type: 'bytes4',
      },
    ],
    name: 'supportsInterface',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'totalVotingRounds',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
    ],
    name: 'unpauseVotingRound',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'owner',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'nonce',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'chainID',
        type: 'uint256',
      },
      {
        internalType: 'bytes',
        name: 'functionSignature',
        type: 'bytes',
      },
      {
        internalType: 'bytes32',
        name: 'sigR',
        type: 'bytes32',
      },
      {
        internalType: 'bytes32',
        name: 'sigS',
        type: 'bytes32',
      },
      {
        internalType: 'uint8',
        name: 'sigV',
        type: 'uint8',
      },
    ],
    name: 'verify',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'starsAmountMantissa',
        type: 'uint256',
      },
      {
        internalType: 'bool',
        name: 'isMogulSmartWallet',
        type: 'bool',
      },
      {
        internalType: 'address',
        name: 'mogulSmartWalletAddress',
        type: 'address',
      },
    ],
    name: 'voteFor',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    name: 'votingRounds',
    outputs: [
      {
        internalType: 'uint256',
        name: 'startVoteBlockNum',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'endVoteBlockNum',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'starsRewards',
        type: 'uint256',
      },
      {
        internalType: 'enum Voting.VotingRoundState',
        name: 'votingRoundState',
        type: 'uint8',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'roundId',
        type: 'uint256',
      },
      {
        internalType: 'bool',
        name: 'isMogulSmartWallet',
        type: 'bool',
      },
      {
        internalType: 'address',
        name: 'mogulSmartWalletAddress',
        type: 'address',
      },
    ],
    name: 'withdrawAndClaimStarsRewards',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'withdrawETH',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
];

const _bytecode =
  '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';

type VotingConstructorParams =
  | [signer?: Signer]
  | ConstructorParameters<typeof ContractFactory>;

const isSuperArgs = (
  xs: VotingConstructorParams,
): xs is ConstructorParameters<typeof ContractFactory> => xs.length > 1;

export class VotingFactory extends ContractFactory {
  constructor(...args: VotingConstructorParams) {
    if (isSuperArgs(args)) {
      super(...args);
    } else {
      super(_abi, _bytecode, args[0]);
    }
  }

  deploy(
    _admin: string,
    _stars: string,
    _mgl: string,
    _votingMasterChef: string,
    overrides?: Overrides & { from?: string | Promise<string> },
  ): Promise<Voting> {
    return super.deploy(
      _admin,
      _stars,
      _mgl,
      _votingMasterChef,
      overrides || {},
    ) as Promise<Voting>;
  }
  getDeployTransaction(
    _admin: string,
    _stars: string,
    _mgl: string,
    _votingMasterChef: string,
    overrides?: Overrides & { from?: string | Promise<string> },
  ): TransactionRequest {
    return super.getDeployTransaction(
      _admin,
      _stars,
      _mgl,
      _votingMasterChef,
      overrides || {},
    );
  }
  attach(address: string): Voting {
    return super.attach(address) as Voting;
  }
  connect(signer: Signer): VotingFactory {
    return super.connect(signer) as VotingFactory;
  }

  static readonly bytecode = _bytecode;
  static readonly abi = _abi;
  static createInterface(): VotingInterface {
    return new utils.Interface(_abi) as VotingInterface;
  }
  static connect(address: string, signerOrProvider: Signer | Provider): Voting {
    return new Contract(address, _abi, signerOrProvider) as Voting;
  }
}
